// PII-free device context for analytics payloads.
//
// The Part B1 allow-list bans raw user agents (and IPs) from the event
// stream, but bug triage needs to know WHAT kind of device saw a problem.
// This module derives coarse, enum-bounded families from navigator.userAgent
// locally — the raw string never leaves the browser — plus viewport and
// network quality readings. Everything degrades to undefined when an API is
// unavailable; emit validation treats absent optionals as fine.
//
// Registry contract (assessment_form_started):
//   device_type    enum(mobile,tablet,desktop,other)
//   browser_family enum(chrome,edge,safari,firefox,samsung_internet,opera,other)
//   os_family      enum(windows,macos,ios,android,chromeos,linux,other)
//   viewport_width / viewport_height  int
//   network_type   enum(slow-2g,2g,3g,4g)   (navigator.connection.effectiveType)
//   downlink_mbps  float

import { getDeviceContext } from '../utils/getDeviceContext';

const NETWORK_TYPES = ['slow-2g', '2g', '3g', '4g'];

// Order matters: several browsers embed competitors' tokens (Edge and Opera
// carry "Chrome"; everything WebKit carries "Safari"), so the most specific
// token is tested first.
export function browserFamilyFromUserAgent(ua) {
  if (!ua) return 'other';
  if (/edg(a|ios|e)?\//i.test(ua)) return 'edge';
  if (/samsungbrowser\//i.test(ua)) return 'samsung_internet';
  if (/(opr|opios)\//i.test(ua)) return 'opera';
  if (/(fxios|firefox)\//i.test(ua)) return 'firefox';
  if (/(crios|chrome)\//i.test(ua)) return 'chrome';
  if (/safari\//i.test(ua) && /version\//i.test(ua)) return 'safari';
  return 'other';
}

export function osFamilyFromUserAgent(ua) {
  if (!ua) return 'other';
  if (/windows/i.test(ua)) return 'windows';
  if (/(iphone|ipad|ipod)/i.test(ua)) return 'ios';
  // iPadOS 13+ masquerades as macOS but still reports touch points.
  if (/macintosh/i.test(ua)) {
    const touchPoints =
      typeof navigator !== 'undefined' ? navigator.maxTouchPoints || 0 : 0;
    return touchPoints > 1 ? 'ios' : 'macos';
  }
  if (/android/i.test(ua)) return 'android';
  if (/cros/i.test(ua)) return 'chromeos';
  if (/linux/i.test(ua)) return 'linux';
  return 'other';
}

export function deviceTypeFromUserAgent(ua) {
  const os = osFamilyFromUserAgent(ua);
  if (!ua) return 'other';
  if (/ipad/i.test(ua)) return 'tablet';
  if (os === 'ios' && /macintosh/i.test(ua)) return 'tablet'; // masquerading iPad
  if (/android/i.test(ua)) return /mobile/i.test(ua) ? 'mobile' : 'tablet';
  if (/(iphone|ipod)/i.test(ua)) return 'mobile';
  if (['windows', 'macos', 'chromeos', 'linux'].includes(os)) return 'desktop';
  return 'other';
}

// Session context beyond the device: where in the world the session sits
// (timezone, not geolocation) and how the person has asked to be shown things.
//
// Location stance: an IANA timezone and a language tag answer the practical
// questions — out-of-hours use, language needs — without an IP-derived
// location ever entering the stream. Coarse geography stays in GA4 under its
// own consent; nothing here narrows to a person or a place.
//
// Accessibility signals are captured because the design standard treats
// contrast, motion and target size as a floor rather than a preference: we
// cannot tell whether reduced-motion users complete forms at the same rate
// without recording that they asked for reduced motion.
export function getAnalyticsSessionContext() {
  const fields = {};
  try {
    if (typeof Intl !== 'undefined' && Intl.DateTimeFormat) {
      const timezone = Intl.DateTimeFormat().resolvedOptions()?.timeZone;
      if (typeof timezone === 'string' && timezone) fields.timezone = timezone;
    }
    if (typeof navigator !== 'undefined' && typeof navigator.language === 'string') {
      if (navigator.language) fields.locale = navigator.language;
    }
    if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
      fields.prefers_reduced_motion = window.matchMedia(
        '(prefers-reduced-motion: reduce)',
      ).matches;
      fields.color_scheme = window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light';
    }
  } catch (_) {
    // Context is a nicety; a missing API must never cost us the event.
  }
  return fields;
}

// Returns only defined fields so payloads stay minimal; safe to spread
// directly into an event payload. Never throws.
//
// The reading itself comes from getDeviceContext — the same capture that
// annotates the Firestore one-time events — so there is one definition of
// "what this device is" and the two destinations cannot drift apart. What this
// function adds is the registry contract: the raw user agent is reduced to
// enum-bounded families and never forwarded, and a value outside an enum is
// dropped rather than passed through.
export function getAnalyticsDeviceFields() {
  try {
    const context = getDeviceContext() || {};
    const ua = context.userAgent || '';

    const fields = {
      device_type: deviceTypeFromUserAgent(ua),
      browser_family: browserFamilyFromUserAgent(ua),
      os_family: osFamilyFromUserAgent(ua),
    };
    if (Number.isInteger(context.viewportWidth)) fields.viewport_width = context.viewportWidth;
    if (Number.isInteger(context.viewportHeight)) fields.viewport_height = context.viewportHeight;
    if (NETWORK_TYPES.includes(context.networkType)) fields.network_type = context.networkType;
    if (Number.isFinite(context.downlinkMbps)) fields.downlink_mbps = context.downlinkMbps;
    return fields;
  } catch (_) {
    return {};
  }
}
